import { DISTRICT_COUNT } from "@/lib/audienceEngine";

export const CONTACT = {
  company: "NicheCom",
  address: ["Oaklands House", "2 Oaklands Park", "Wokingham RG41 2FD"],
  phone: "0118 977 0690",
  phoneHref: "tel:+441189770690",
  email: "info@nichecom.co.uk",
  site: "https://www.nichecom.co.uk",
};

export const STORY_STEPS = [
  { n: "01", title: "Identify your patch", body: "Add the postcode districts your branch covers — one, or fifty." },
  { n: "02", title: "Find relevant audiences", body: "See how many established homeowners sit within each life-stage and property profile." },
  { n: "03", title: "Build the campaign", body: "Choose your reach, format and message in a single, considered brief." },
  { n: "04", title: "Land on the doormat", body: "NicheCom Print produces and delivers it to the homes that match." },
  { n: "05", title: "Start the conversation", body: "Be the agent they already recognise when the time comes to move." },
];

export const FLOW_STORY = ["Postcode", "Data", "Audience", "Letter", "Home", "Conversation"];

export const PERSONAS = [
  {
    id: "downsizer",
    name: "The Downsizer",
    lines: ["Established homeowner", "3–5 bedroom property", "Potential next move: a smaller home"],
    match: (g) => g.downsize && g.bedrooms >= 3,
    motif: "large",
  },
  {
    id: "equity",
    name: "The Equity Mover",
    lines: ["Long-established homeowner", "Potential capital release", "Property-rich audience"],
    match: (g) => g.capitalRelease,
    motif: "keys",
  },
  {
    id: "retirement",
    name: "The Retirement Mover",
    lines: ["Retired household", "Potential lifestyle move", "Relevant retirement signals"],
    match: (g) => g.retirement === "RET",
    motif: "horizon",
  },
  {
    id: "village",
    name: "The Village Retiree",
    lines: ["Established rural homeowner", "Larger property", "Potential downsizing or lifestyle move"],
    match: (g) => ["C08", "C11", "M47"].includes(g.code),
    motif: "village",
  },
  {
    id: "later",
    name: "The Later-Life Mover",
    lines: ["Smaller established household", "Retirement-stage audience", "Potential supported or sheltered move"],
    match: (g) => g.sheltered && g.bedrooms <= 3,
    motif: "small",
  },
];

// Supplied marketing claims — sources must be verified before production.
export const KEY_INSIGHTS = [
  {
    id: "audience", n: "01", theme: "lime", kicker: "The audience is changing", art: "houses",
    title: "The next move might come from the generation that already owns a home.",
    paragraphs: [
      { text: "Downsizing is a significant part of today's property market. For estate agents, that means a valuable audience with different needs, different motivations — and potentially a different response to marketing." },
      { text: "They aren't starting their property journey. They're changing it.", bold: true },
    ],
    stat: "37%", statLabel: "of 2025 UK homebuyers were downsizers", source: "Rightmove",
  },
  {
    id: "mail", n: "02", theme: "navy", kicker: "Mail still carries weight", art: "envelope",
    title: "When something arrives through the letterbox, it feels more considered.",
    paragraphs: [
      { text: "In a world of scrolling feeds and crowded inboxes, physical mail has something digital can't replicate: a message that has been deliberately delivered to someone's home." },
      { text: "And that can make it feel more credible, more personal and more worth reading." },
    ],
    stat: "87%", statLabel: "find messages delivered by mail believable", source: "JICMAIL",
  },
  {
    id: "letterbox", n: "03", theme: "light", kicker: "Less competition for attention", art: "letterbox",
    title: "The letterbox is quieter than it used to be.",
    paragraphs: [
      { text: "With dramatically fewer letters being delivered today, direct mail isn't competing with the volume it once did." },
      { text: "For estate agents, that creates an opportunity to put a physical message directly into the hands — and homes — of potential movers." },
      { text: "Less noise. More chance to be noticed.", bold: true },
    ],
    stat: "-77%", statLabel: "fewer letters delivered per person since 2000", detail: "339 → 78 letters per year", source: "Ofcom, 2025",
  },
  {
    id: "digital", n: "04", theme: "forest", kicker: "Digital doesn't reach everyone equally", art: "phone",
    title: "The people you're trying to reach aren't necessarily scrolling.",
    paragraphs: [
      { text: "Social media has transformed property marketing, but it shouldn't be the only conversation." },
      { text: "For older homeowners in particular, traditional channels can still play an important role in how information is discovered, trusted and acted upon.", highlight: true },
      { text: "The smartest campaigns don't choose digital or direct mail. They make them work together.", bold: true },
    ],
    stat: "28%", statLabel: "of 65–74s trust social media as a source", source: "Ofcom, 2025",
  },
];

export const BOTTOM_LINE = {
  question: "Stop overlooking a proven way to reach these local homeowners.",
};

export const CAMPAIGN_ANGLES = [
  { id: "downsizing", title: "Thinking of downsizing?", quote: "You may not need all that space. Your next move could give you more freedom.", bestSuited: "Downsize = Yes", fit: (g) => g.downsize, creative: "downsizing" },
  { id: "unlock", title: "What could your home unlock?", quote: "Years of ownership may have created more options than you realise.", bestSuited: "Capital release = Yes", fit: (g) => g.capitalRelease, creative: "equity", note: "Marketing concept only — not financial advice." },
  { id: "easier", title: "Ready for something easier?", quote: "A smaller home. Less maintenance. More time for everything else.", bestSuited: "Downsizers / retirement stage", fit: (g) => g.downsize || g.retirement === "RET", creative: "retirement" },
  { id: "chapter", title: "Your next chapter", quote: "Moving home isn't always about needing more space.", bestSuited: "Broad homeowner audience", fit: () => true, creative: "valuation" },
];

export const CREATIVE_THEMES = [
  { id: "downsizing", label: "Downsizing", envelope: "Thinking of downsizing?" },
  { id: "equity", label: "Unlocking equity", envelope: "What could your home unlock?" },
  { id: "retirement", label: "Retirement move", envelope: "Ready for something easier?" },
  { id: "market", label: "Local market update", envelope: "Your local market, this season" },
  { id: "valuation", label: "Free valuation", envelope: "What is your home worth now?" },
  { id: "custom", label: "Custom campaign", envelope: "Your message here" },
];

export const FORMATS = [
  { id: "letter", label: "Letter", body: "Personal, direct and ideal for considered messaging." },
  { id: "postcard", label: "Postcard", body: "High-impact, immediate and visual." },
  { id: "unsure", label: "Your recommendation", body: "Let the Print team recommend the right format for this audience." },
];

export const SERVICES = [
  { id: "print", label: "Print production" },
  { id: "personalisation", label: "Personalisation" },
  { id: "envelope", label: "Envelope" },
  { id: "postage", label: "Postage" },
  { id: "design", label: "Design support" },
  { id: "copy", label: "Campaign copy" },
  { id: "qr", label: "QR code" },
  { id: "landing", label: "Dedicated valuation landing page" },
];

export const COMPILATION_STAGES = [
  "Validating target areas",
  "Mapping postcode data",
  "Matching audience profiles",
  "Applying campaign criteria",
  "Calculating reach",
  "Building campaign",
];
